import { useId, useState, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../../components/liks/LiksDialog/LiksDialog';
import { LiksTextFormField } from '../../../components/liks/LiksTextFormField/LiksTextFormField';
import { useI18n } from '../../../i18n/useI18n';
import { isPasswordPolicyCompliant } from '../../../utils/passwordPolicyUtils';
import { PasswordPolicyChecklist } from './PasswordPolicyChecklist';
import './ResetUserPasswordDialog.css';

type ResetUserPasswordDialogProps = {
  onReset: (password: string, temporary: boolean) => void;
  onClose: () => void;
  isSubmitting?: boolean;
};

export function ResetUserPasswordDialog({
  onReset,
  onClose,
  isSubmitting = false,
}: ResetUserPasswordDialogProps) {
  const { translate } = useI18n();
  const temporaryFieldId = useId();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [temporary, setTemporary] = useState(true);

  const trimmedPassword = password.trim();
  const isSubmitDisabled =
    isSubmitting || !isPasswordPolicyCompliant(trimmedPassword);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitDisabled) {
      return;
    }
    onReset(trimmedPassword, temporary);
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="sm"
      title={translate('lac.users.profile.resetPasswordTitle')}
      onClose={onClose}
      onSubmit={handleSubmit}
      closeDisabled={isSubmitting}
      isDismissDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {translate('lac.users.profile.resetPasswordCancel')}
          </LiksButton>
          <LiksButton type="submit" disabled={isSubmitDisabled}>
            {translate('lac.users.profile.resetPasswordSubmit')}
          </LiksButton>
        </>
      }
    >
      <p className="reset-user-password__description">
        {translate('lac.users.profile.resetPasswordDescription')}
      </p>

      <LiksTextFormField
        label={translate('lac.users.profile.resetPasswordPasswordLabel')}
        required
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={setPassword}
        autoFocus
        disabled={isSubmitting}
        autoComplete="new-password"
        trailing={
          <button
            type="button"
            className="liks-text-form-field__trailing-button"
            aria-label={translate(
              showPassword
                ? 'lac.users.profile.resetPasswordHidePassword'
                : 'lac.users.profile.resetPasswordShowPassword',
            )}
            onClick={() => setShowPassword((current) => !current)}
            disabled={isSubmitting}
          >
            {showPassword ? (
              <EyeOff strokeWidth={2} aria-hidden />
            ) : (
              <Eye strokeWidth={2} aria-hidden />
            )}
          </button>
        }
      />

      <PasswordPolicyChecklist
        password={trimmedPassword}
        policyKeyPrefix="lac.users.profile.resetPasswordPolicy"
      />

      <label
        className="reset-user-password__checkbox"
        htmlFor={temporaryFieldId}
      >
        <input
          id={temporaryFieldId}
          type="checkbox"
          checked={temporary}
          onChange={(event) => setTemporary(event.target.checked)}
          disabled={isSubmitting}
        />
        <span>
          {translate('lac.users.profile.resetPasswordTemporaryLabel')}
        </span>
      </label>
    </LiksDialog>
  );
}
